import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

function formatError(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d.msg || JSON.stringify(d);
}

function AuthBrandMark() {
  return (
    <Link to="/" className="flex items-center justify-center mb-10" data-testid="auth-brandmark" aria-label="Accommodation Suppliers — home">
      <img
        src="/brand/as-logo-stacked.png"
        alt="Accommodation Suppliers"
        className="h-24 w-auto"
      />
    </Link>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // One-shot success toast after password reset
  useEffect(() => {
    const t = sessionStorage.getItem("auth_toast");
    if (t) {
      toast.success(t);
      sessionStorage.removeItem("auth_toast");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      // Preserve pricing → dashboard upgrade intent so Stripe checkout can auto-launch post-login
      const upgrade = searchParams.get("upgrade");
      if (upgrade && u.role === "advertiser") {
        navigate(`/dashboard?upgrade=${encodeURIComponent(upgrade)}`);
      } else {
        navigate(u.role === "admin" ? "/admin" : u.role === "buyer" ? "/buyer" : "/dashboard");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="login-page">
      <AuthBrandMark />
      <div className="tracking-overline mb-3">Sign in</div>
      <h1 className="font-serif text-3xl text-[#11211A]">Welcome back</h1>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#4A5550] mb-1">Email</label>
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="login-email" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-[#4A5550]">Password</label>
            <Link to="/forgot-password" className="text-xs text-[#264839] hover:underline" data-testid="forgot-password-link">
              Forgot Password?
            </Link>
          </div>
          <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="login-password" />
        </div>
        <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">{loading ? "Signing in…" : "Sign in"}</button>
      </form>
      <p className="text-sm text-[#7D8C85] mt-6 text-center">
        No account? <Link to={`/register${searchParams.get("upgrade") ? `?upgrade=${encodeURIComponent(searchParams.get("upgrade"))}` : ""}`} className="text-[#264839] hover:underline">Create one</Link>
      </p>
    </div>
  );
}


export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch {
      // Same UX for any error — no email enumeration
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="forgot-password-page">
      <AuthBrandMark />
      <div className="tracking-overline mb-3">Password reset</div>
      <h1 className="font-serif text-3xl text-[#11211A]">Forgot your password?</h1>
      <p className="text-sm text-[#4A5550] mt-3">
        Drop your email — we'll send you a one-time link to set a new one. The link expires in 30 minutes.
      </p>
      {sent ? (
        <div className="mt-8 border border-[#264839]/20 bg-[#F0EFEA] p-5 text-sm text-[#11211A]" role="status" data-testid="forgot-password-success">
          If an account exists for <strong>{email}</strong>, we've sent a reset link. Check your inbox
          (and spam folder). The link is valid for 30 minutes and can be used once.
          <div className="mt-4">
            <Link to="/login" className="text-[#264839] hover:underline text-sm">← Back to sign in</Link>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Email</label>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="forgot-password-email" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="forgot-password-submit">
            {busy ? "Sending…" : "Send reset link"}
          </button>
          <div className="text-center">
            <Link to="/login" className="text-xs text-[#7D8C85] hover:text-[#264839]">← Back to sign in</Link>
          </div>
        </form>
      )}
    </div>
  );
}


export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const isClaim = params.get("claim") === "1";
  const [valid, setValid] = useState(null); // null | true | false
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setValid(false); return; }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setValid(!!data.valid))
      .catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirmPw) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      if (data?.claim && data?.user) {
        setUser(data.user);
        toast.success("Listing activated — welcome to your supplier dashboard");
        navigate("/dashboard", { replace: true });
        return;
      }
      sessionStorage.setItem("auth_toast", "Password reset successful. Please sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  if (valid === null) {
    return <div className="max-w-md mx-auto px-4 py-20 text-center text-sm text-[#7D8C85]" data-testid="reset-password-loading">Checking your link…</div>;
  }
  if (!valid) {
    return (
      <div className="max-w-md mx-auto px-4 py-20" data-testid="reset-password-invalid">
        <div className="tracking-overline mb-3 text-[#B35C4A]">Link expired</div>
        <h1 className="font-serif text-3xl text-[#11211A]">This {isClaim ? "claim" : "reset"} link is no longer valid.</h1>
        <p className="text-sm text-[#4A5550] mt-3">
          It may have expired, already been used, or been generated by a different request. You can request a new one below.
        </p>
        <div className="mt-6 flex gap-3">
          <Link to="/forgot-password" className="btn-primary">Request new link</Link>
          <Link to="/login" className="btn-secondary">Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="reset-password-page">
      <AuthBrandMark />
      <div className="tracking-overline mb-3 text-[#B35C4A]">{isClaim ? "Claim your listing · Accommodation Suppliers" : "Set a new password"}</div>
      <h1 className="font-serif text-3xl text-[#11211A]">{isClaim ? "Activate your account" : "Choose a new password"}</h1>
      <p className="text-sm text-[#4A5550] mt-3">
        {isClaim
          ? "Set a password to activate your Founding Supplier listing. The 'Unclaimed' badge will be removed automatically once you're in."
          : "At least 6 characters. Use something you can remember but nobody else can guess."}
      </p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#4A5550] mb-1">{isClaim ? "Create your password" : "New password"}</label>
          <input required type="password" minLength={6} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="reset-password-new" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4A5550] mb-1">Confirm password</label>
          <input required type="password" minLength={6} autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="reset-password-confirm" />
        </div>
        {error && <p className="text-xs text-[#B35C4A]" role="alert" data-testid="reset-password-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="reset-password-submit">
          {busy ? "Saving…" : (isClaim ? "Activate my listing" : "Reset password")}
        </button>
      </form>
    </div>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState(searchParams.get("upgrade") || searchParams.get("role") === "advertiser" ? "advertiser" : "buyer");
  const isFreePlan = searchParams.get("plan") === "free";
  const [form, setForm] = useState({ name: "", email: "", company: "", website: "", password: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dupMatch, setDupMatch] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (role !== "advertiser") { setDupMatch(null); return; }
    const website = form.website.trim(), company = form.company.trim();
    if (website.length < 4 && company.length < 3) { setDupMatch(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/check-company?website=${encodeURIComponent(website)}&company=${encodeURIComponent(company)}`);
        setDupMatch(data.match);
      } catch { setDupMatch(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.website, form.company, role]);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please accept the Terms of Use and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const u = await register({ ...form, role });
      toast.success("Account created");
      const upgrade = searchParams.get("upgrade");
      if (upgrade && u.role === "advertiser") {
        navigate(`/dashboard?upgrade=${encodeURIComponent(upgrade)}`);
      } else {
        navigate(u.role === "buyer" ? "/buyer" : "/dashboard");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="register-page">
      <AuthBrandMark />
      <div className="tracking-overline mb-3">Create account</div>
      <h1 className="font-serif text-3xl text-[#11211A]">{isFreePlan ? "List your company free" : "Join Accommodation Suppliers"}</h1>
      {isFreePlan && (
        <p className="text-sm text-[#4A5550] mt-3 leading-relaxed" data-testid="register-free-plan-note">
          Create your account, then add your company listing from the dashboard. Free forever — name, description, category, country and website. Upgrade to Founding Supplier any time to receive RFQs.
        </p>
      )}

      <div className="mt-8 grid grid-cols-2 gap-3">
        <button type="button" onClick={() => setRole("buyer")} className={`p-4 text-left border ${role === "buyer" ? "border-[#264839] bg-white" : "border-[#E8ECE9] bg-transparent"}`} data-testid="role-buyer">
          <div className="tracking-overline text-[10px] mb-1">I'm a</div>
          <div className="font-serif text-lg text-[#11211A]">Buyer</div>
          <div className="text-xs text-[#7D8C85] mt-1">Source products for my hotel</div>
        </button>
        <button type="button" onClick={() => setRole("advertiser")} className={`p-4 text-left border ${role === "advertiser" ? "border-[#264839] bg-white" : "border-[#E8ECE9] bg-transparent"}`} data-testid="role-advertiser">
          <div className="tracking-overline text-[10px] mb-1">I'm a</div>
          <div className="font-serif text-lg text-[#11211A]">Supplier</div>
          <div className="text-xs text-[#7D8C85] mt-1">List products & receive RFQs</div>
        </button>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#4A5550] mb-1">Full name</label>
          <input required value={form.name} onChange={set("name")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="register-name" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4A5550] mb-1">{role === "buyer" ? "Hotel / Company" : "Company"}</label>
          <input value={form.company} onChange={set("company")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="register-company" />
        </div>
        {role === "advertiser" && (
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Company website <span className="text-[#9AA6A0] font-normal">(optional)</span></label>
            <input type="text" inputMode="url" placeholder="yourcompany.com" value={form.website} onChange={set("website")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="register-website" />
          </div>
        )}
        {dupMatch && (
          <div className="border-l-4 border-[#B8860B] bg-[#FFFBEB] px-4 py-3 text-sm text-[#4A5550]" data-testid="register-duplicate-warning">
            <div className="font-medium text-[#11211A]">
              {dupMatch.is_unclaimed ? "Good news — your company is already listed." : "Heads up — this company is already in the directory."}
            </div>
            <div className="text-xs mt-1 leading-relaxed">
              <strong>{dupMatch.company}</strong>{dupMatch.website ? ` (${dupMatch.website.replace(/^https?:\/\//, "")})` : ""} matched on {dupMatch.matched_on}.{" "}
              {dupMatch.is_unclaimed
                ? <>Instead of creating a second account, claim the existing listing{dupMatch.email_hint ? <> — we'll email the claim link to <span className="font-mono">{dupMatch.email_hint}</span></> : ""}.</>
                : <>If that's your company, <Link to="/login" className="underline">log in</Link> to manage it or ask a colleague to invite you. If it's a different business with a similar name, carry on.</>}
            </div>
            {dupMatch.is_unclaimed && (
              <Link to={`/listing/${dupMatch.listing_id}?claim=1`} className="inline-flex mt-2 text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 bg-[#264839] text-white hover:bg-[#1B342A] transition-colors" data-testid="register-claim-existing-btn">
                Claim the existing listing →
              </Link>
            )}
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-[#4A5550] mb-1">Email</label>
          <input required type="email" value={form.email} onChange={set("email")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="register-email" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4A5550] mb-1">Password</label>
          <input required type="password" minLength={6} value={form.password} onChange={set("password")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="register-password" />
        </div>
        <label className="flex items-start gap-2.5 text-xs text-[#4A5550] leading-relaxed cursor-pointer select-none" data-testid="register-consent-wrapper">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 accent-[#264839] w-4 h-4 flex-shrink-0"
            data-testid="register-consent"
          />
          <span>
            I agree to the{" "}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-[#264839] underline hover:text-[#11211A]" data-testid="register-terms-link">Terms of Use</Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#264839] underline hover:text-[#11211A]" data-testid="register-privacy-link">Privacy Policy</Link>.
          </span>
        </label>
        <button type="submit" disabled={loading || !consent} className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-testid="register-submit">{loading ? "Creating…" : "Create account"}</button>
      </form>
      <p className="text-sm text-[#7D8C85] mt-6 text-center">
        Already a member? <Link to={`/login${searchParams.get("upgrade") ? `?upgrade=${encodeURIComponent(searchParams.get("upgrade"))}` : ""}`} className="text-[#264839] hover:underline">Sign in</Link>
      </p>
    </div>
  );
}
